import { Country, NotFoundResponse } from '@/types/api/common';

export const ENERGY_TYPE = {
  ELECTRICITY: 'ELECTRICITY',
  DISTRICT_HEATING: 'DISTRICT_HEATING',
  GAS: 'GAS',
  WATER: 'WATER',
} as const;

export const EL_CERT_RATE = {
  HIGH_RATE: 'HIGH_RATE',
  LOW_RATE: 'LOW_RATE',
  NO_RATE: 'NO_RATE',
} as const;

export const POWER_SUPPLIER = {
  FORTUM_NORWAY: 'FORTUM_NORWAY',
  FORTUM_SWEDEN: 'FORTUM_SWEDEN',
  FORTUM_FINLAND: 'FORTUM_FINLAND',
  NTE_NORWAY: 'NTE_NORWAY',
} as const;

export const METER_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  PLANNED: 'Planned',
  CONFIRMED: 'Confirmed',
} as const;

export type MeterStatus = (typeof METER_STATUS)[keyof typeof METER_STATUS];

export const CUSTOM_METER_PROPERTY_TYPE = { NICKNAME: 'NickName' } as const;

export const METERING_METHOD = { MANUALLY: 'Manually', REMOTELY: 'Remotely' } as const;

export type EnergyType = (typeof ENERGY_TYPE)[keyof typeof ENERGY_TYPE];
export type MeteringMethod = (typeof METERING_METHOD)[keyof typeof METERING_METHOD];

export type ElCertRate = (typeof EL_CERT_RATE)[keyof typeof EL_CERT_RATE];
export type PowerSupplier = (typeof POWER_SUPPLIER)[keyof typeof POWER_SUPPLIER];
export type CustomMeterPropertyType =
  (typeof CUSTOM_METER_PROPERTY_TYPE)[keyof typeof CUSTOM_METER_PROPERTY_TYPE];

export type AddressModel = { address: string | null; zipCode: string | null; city: string | null };

export type GroupParentModel = { id: number | null; name: string | null };

export type CustomerModel = { customerid: number; customerName: string | null };

export type AgreementCustomerModel = CustomerModel & { groupParent: GroupParentModel };

export type AgreementModel = {
  agreementId: number;
  agreementDate: string | null;
  agreementCustomer: AgreementCustomerModel;
};

export type MeterCustomerModel = {
  customerid: number | null;
  customerName: string | null;
  customerBusinessId: string | null;
};

export type GridOwnerModel = { id: number | null; name: string | null };

export type PowerSupplierModel = {
  id: number | null;
  name: string | null;
  powerSupplierType: PowerSupplier;
};

export type PortfolioModel = {
  id: number;
  name: string | null;
  supplierAgreementName: string | null;
  powerSupplier: PowerSupplierModel;
};

export type Meter = {
  meteringMethod: MeteringMethod;
  energyType: EnergyType;
  country: Country;
  meterPointId: string | null;
  meterNumber: string | null;
  meterDeliveryDate: string | null;
  nickName: string | null;
  meterPlannedStopDate: string | null;
  meterStopDate: string | null;
  meterStatus: MeterStatus | null;
  estimatedYearlyConsumption: number | null;
  invoiceReference: string | null;
  supplierCustomerId: string | null;
  elCertPercentage: number | null;
  elCertRateType: ElCertRate;
  priceArea: string | null;
  meterDescription: string | null;
  microProduction: boolean;
  invoiceType: string | null;
  portfolio: PortfolioModel;
  agreement: AgreementModel;
  meterCustomer: MeterCustomerModel;
  gridOwner: GridOwnerModel;
  meterAddress: AddressModel;
  invoiceAddress: AddressModel;
  inactiveMeter: boolean;
};

type AddressWithMeters = AddressModel & { meters: Meter[] | null };

export type MeterCustomer = MeterCustomerModel & { meterAddress: AddressWithMeters[] | null };

export type MainCustomerResponse = MainCustomer[] | NotFoundResponse;

export type MeterResponse = Meter[] | NotFoundResponse;

export const isMainCustomerData = (data: MainCustomerResponse): data is MainCustomer[] => {
  return Array.isArray(data);
};

export const isMeterData = (data: MeterResponse): data is Meter[] => {
  return Array.isArray(data);
};

export type MainCustomer = {
  mainCustomerid: number;
  mainCustomerName: string | null;
  meterCustomers: MeterCustomer[] | null;
};

export type MeterExportRequest = { returnInactiveMeters: boolean; meterPointIds: string[] };

export type EditNicknameRequest = {
  country: Country;
  meterPropertyType: CustomMeterPropertyType;
  propertyValue: string;
  meterPointId: string;
  customerId: string;
};
